import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ASSETS, VENTURES } from "@/lib/brand";
import Seo from "@/components/Seo";

export default function Ventures() {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const id = hash.replace("#", "");
      const el = document.getElementById(id);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
      }
    } else {
      window.scrollTo({ top: 0 });
    }
  }, [hash]);

  return (
    <div data-testid="ventures-page" className="pt-32 pb-20">
      <Seo
        title="Ventures · MPR Ventures"
        description="The five operating companies of MPR Ventures — Court Street Group (Las Vegas), Pacific Standard Brands (Los Angeles), Skyline Global (New York), Santana Enterprises (Miami), and the Better Tomorrow Initiative (Washington DC)."
        path="/ventures"
      />
      {/* Header */}
      <div className="px-6 md:px-12">
        <div className="max-w-[1440px] mx-auto">
          <p className="font-overline text-champagne mb-6">— The Conglomerate</p>
          <h1 className="font-display text-5xl md:text-7xl lg:text-[7rem] text-silver leading-[0.92] tracking-tighter max-w-6xl">
            MPR Ventures<br />
            <span className="italic font-light">& its operating companies.</span>
          </h1>
          <p className="mt-12 max-w-2xl font-body text-silver-muted text-lg leading-relaxed">
            A holding firm of five operating companies, each headquartered in a city that defines a
            discipline. Below: a profile of each venture, in order of formation.
          </p>
        </div>
      </div>

      {/* Index of ventures */}
      <div className="px-6 md:px-12 mt-20">
        <div className="max-w-[1440px] mx-auto border-y hairline">
          {VENTURES.map((v, i) => (
            <a
              key={v.id}
              href={`#${v.id}`}
              data-testid={`venture-index-${v.id}`}
              className="grid grid-cols-12 gap-4 py-5 border-b hairline last:border-b-0 hover:bg-white/[0.03] transition-colors duration-300 px-2"
            >
              <span className="col-span-2 md:col-span-1 font-overline text-silver-muted">{String(i + 1).padStart(2, "0")}</span>
              <span className="col-span-7 md:col-span-6 font-display text-xl md:text-2xl text-silver">{v.name}</span>
              <span className="col-span-3 md:col-span-3 font-overline text-silver-muted">{v.city}</span>
              <span className="col-span-12 md:col-span-2 text-right font-overline text-champagne hidden md:block">View ↓</span>
            </a>
          ))}
        </div>
      </div>

      {/* Individual venture profiles */}
      <div className="mt-32 space-y-32 md:space-y-48">
        {VENTURES.map((v, i) => (
          <section
            key={v.id}
            id={v.id}
            data-testid={`venture-section-${v.id}`}
            className="relative px-6 md:px-12 scroll-mt-24"
          >
            <div className="max-w-[1440px] mx-auto">
              <div className="grid grid-cols-12 gap-8 md:gap-16 items-stretch">
                {/* Left visual */}
                <div className={`col-span-12 lg:col-span-7 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                  <div className="relative aspect-[16/10] border hairline overflow-hidden group">
                    <img
                      src={ASSETS.cityBg[v.bg === "lasVegas" ? "lasVegas" : v.bg === "losAngeles" ? "losAngeles" : v.bg === "newYork" ? "newYork" : v.bg === "miami" ? "miami" : "dc"]}
                      alt={v.city}
                      className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#050A15] via-[#050A15]/30 to-transparent" />
                    <div className="absolute inset-0 grain" />
                    <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10 flex items-end justify-between">
                      <div>
                        <p className="font-overline text-champagne">{v.region}</p>
                        <p className="font-display text-3xl md:text-5xl text-silver tracking-tighter mt-2">{v.city}</p>
                      </div>
                      <span className="font-overline text-silver-muted">{String(i + 1).padStart(2, "0")} / 05</span>
                    </div>
                  </div>
                </div>

                {/* Right text */}
                <div className={`col-span-12 lg:col-span-5 flex flex-col justify-center ${i % 2 === 1 ? "lg:order-1 lg:pr-12 lg:border-r hairline" : "lg:pl-12 lg:border-l hairline"}`}>
                  <p className="font-overline text-silver-muted">— Operating Company {String(i + 1).padStart(2, "0")}</p>
                  <h2 className="mt-4 font-display text-4xl md:text-5xl lg:text-6xl text-silver tracking-tighter leading-[1.02]">
                    {v.name}
                  </h2>
                  <p className="mt-6 font-body text-lg text-silver leading-relaxed">
                    {v.tagline}
                  </p>
                  <p className="mt-6 font-body text-silver-muted leading-relaxed">
                    {v.description}
                  </p>

                  <dl className="mt-10 grid grid-cols-2 gap-px bg-white/10 border hairline">
                    <div className="bg-navy p-5">
                      <dt className="font-overline text-silver-muted">Headquarters</dt>
                      <dd className="font-display text-xl text-silver mt-2">{v.city}</dd>
                    </div>
                    <div className="bg-navy p-5">
                      <dt className="font-overline text-silver-muted">Parent</dt>
                      <dd className="font-display text-xl text-silver mt-2">MPR Ventures</dd>
                    </div>
                  </dl>

                  <div className="mt-10">
                    <Link to="/contact" data-testid={`venture-inquiry-${v.id}`} className="btn-editorial">
                      Inquiries for {v.name.split(" ")[0]}
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
